// src/screens/seller/returns/ReturnsManagement.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import {
  FiSearch,
  FiX,
  FiRotateCcw,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";
import { useTheme } from "../../../contexts/ThemeContext";
import SellerMasterLayout from "../../../layouts/seller/SellerMasterLayout";
import AppContext from "../../../contexts/AppContext";
import "../../../assets/css/seller/screens/SellerReturnsManagement.css";

const STATUS_OPTIONS = ["All Status", "Pending", "Cancelled", "Returned"];

const SellerReturnsManagement = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();


  const { baseUrl, sellerAuthToken, setSellerSelectedReturn } =
    useContext(AppContext);


  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [statsData, setStatsData] = useState(null);

  const getSellerReturns = () => {
    setLoading(true);
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const formdata = new FormData();
    formdata.append("seller_id", seller?.id);
    axios
      .post(`${baseUrl}/seller/return/getSellerReturns`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${sellerAuthToken}`,
        },
      })
      .then((response) => {
        setReturns(response.data.returns ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const getStats = () => {
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const fd = new FormData();
    fd.append("seller_id", seller?.id);
    axios
      .post(`${baseUrl}/seller/return/stats`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${sellerAuthToken}`,

        },
      })

      .then((res) => setStatsData(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    getSellerReturns();
    getStats();
  }, []);

  const handleViewReturn = (returnRequest) => {
    setSellerSelectedReturn(returnRequest);
    navigate("/seller/returns/return-detail");
  };

  const getFilteredReturns = () => {
    let filtered = returns;
    if (statusFilter !== "All Status") {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }
    if (search.trim()) {
      filtered = filtered.filter(
        (r) =>
          r.chorder_id?.toString().includes(search) ||
          r.order?.full_name?.toLowerCase().includes(search.toLowerCase()),
      );
    }
    return filtered;
  };

  const filteredReturns = getFilteredReturns();

  const getStatusClass = (status) => {
    const map = {
      Pending: "rtm-status-pending",
      Cancelled: "rtm-status-cancelled",
      Returned: "rtm-status-returned",
    };
    return map[status] || "";
  };

  return (
    <SellerMasterLayout>
      <div className={`rtm-shell ${isDark ? "dark-mode" : ""}`}>
        <main className="rtm-main">
          <section className="rtm-card-shell">
            {/* ── Stat Cards ── */}
            <div className="rtm-overview">
              <div className="rtm-overview-card">
                <div>
                  <span className="rtm-overview-label">Total Requests</span>
                  <h3 className="rtm-overview-value">
                    {statsData?.total_requests ?? "0"}
                  </h3>
                  <p className="rtm-overview-sub">All return requests</p>
                </div>
                <div className="rtm-overview-icon">
                  <FiRotateCcw />
                </div>
              </div>
              <div className="rtm-overview-card">
                <div>
                  <span className="rtm-overview-label">Pending</span>
                  <h3 className="rtm-overview-value">
                    {statsData?.pending_requests ?? "0"}
                  </h3>
                  <p className="rtm-overview-sub">Awaiting your response</p>
                </div>
                <div className="rtm-overview-icon rtm-overview-icon-alert">
                  <FiClock />
                </div>
              </div>
              <div className="rtm-overview-card">
                <div>
                  <span className="rtm-overview-label">Resolved</span>
                  <h3 className="rtm-overview-value">
                    {statsData?.resolved_requests ?? "0"}
                  </h3>
                  <p className="rtm-overview-sub">Cancelled or returned</p>
                </div>
                <div className="rtm-overview-icon">
                  <FiCheckCircle />
                </div>
              </div>
            </div>

            {/* ── Header / Filters ── */}
            <div className="rtm-header">
              <div className="rtm-header-filters">
                <div className="rtm-search">
                  <FiSearch size={15} />
                  <input
                    placeholder="Search by order ID or customer"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      className="rtm-clear-input-btn"
                      onClick={() => setSearch("")}
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
                <select
                  className="rtm-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* ── Table ── */}
            <div className="rtm-table-wrapper">
              <table className="rtm-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Reason</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        <Spinner
                          animation="border"
                          size="sm"
                          style={{ color: "#ff8559" }}
                        />
                        <span className="ms-2 rtm-muted">
                          Loading return requests...
                        </span>
                      </td>
                    </tr>
                  )}

                  {!loading && filteredReturns.length === 0 && (
                    <tr>
                      <td colSpan={6} className="rtm-no-results-row">
                        No return requests found.
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    filteredReturns.length > 0 &&
                    filteredReturns.map((ret) => (
                      <tr key={ret.id}>
                        <td className="rtm-order-id-cell">#{ret.chorder_id}</td>
                        <td>
                          <div className="rtm-cell-main">
                            <div className="rtm-thumb">
                              {ret.order?.full_name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="rtm-cell-title">
                                {ret.order?.full_name}
                              </p>
                              <span className="rtm-cell-sub">
                                {ret.order?.phone}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>{ret.reason}</td>
                        <td>
                          {new Date(ret.created_at).toLocaleDateString(
                            "en-PK",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </td>
                        <td>
                          <span
                            className={`rtm-status ${getStatusClass(ret.status)}`}
                          >
                            {ret.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="rtm-link"
                            onClick={() => handleViewReturn(ret)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </SellerMasterLayout>
  );
};

export default SellerReturnsManagement;